'use client';

import { useState } from 'react';
import {
  type BaseComponentProps,
  type ContentfulImageType,
  filterEntriesByType,
} from '@bedrock/core/contentful';
import { cn } from '@bedrock/ui/utils';
import { type ContentfulComposableElementType, type ContentfulPDFType } from 'lib/contentful/types';
import PDFViewer from 'ui/common/pdf-viewer/pdf-viewer';
import { MediaImage } from 'ui/components';

type MenuProps = BaseComponentProps<ContentfulComposableElementType>;

function getLabel(frame: ContentfulPDFType): string {
  return frame.pdfTabHeader?.replace(/flipsnack\s*/i, '').trim() ?? '';
}

function Menu({ fields }: Readonly<MenuProps>) {
  const inlineFrames = filterEntriesByType<ContentfulPDFType>(fields.elements, 'dataPdf');
  const [imageEl] = filterEntriesByType<ContentfulImageType>(fields.elements, 'image');

  const [activeIndex, setActiveIndex] = useState(0);
  const activeFrame = inlineFrames[activeIndex];

  if (!inlineFrames.length) return null;

  return (
    <section
      aria-label="Menu section"
      className={cn(
        'relative min-h-[700px] w-full bg-black py-[53px]',
        inlineFrames.length > 1 ? 'md:py-0' : 'md:py-5xl',
      )}
    >
      {inlineFrames.length > 1 ? (
        <div className="relative z-10 px-6 md:px-[5.5%]">
          <nav
            aria-label="Menu filter"
            className="flex w-full items-center justify-start gap-[19px] overflow-x-auto py-[var(--spacing-sm)] pl-[16px] md:mt-[54px] md:gap-[32px] md:pl-0 2xl:pl-[32px]"
          >
            {inlineFrames.map((frame, index) => (
              <button
                key={frame.pdf.sysId}
                aria-pressed={index === activeIndex}
                type="button"
                className={cn(
                  'br-type-title-xl text-foreground min-h-6 shrink-0 border-b-2 border-transparent py-[var(--spacing-sm)] text-center transition-colors duration-200',
                  'hover:text-[hsl(var(--colors-secondary))]',
                  index === activeIndex &&
                    'border-[hsl(var(--colors-secondary))] text-[hsl(var(--colors-secondary))]',
                )}
                onClick={() => {
                  setActiveIndex(index);
                }}
              >
                {getLabel(frame)}
              </button>
            ))}
          </nav>
        </div>
      ) : null}

      {activeFrame ? (
        <>
          <PDFViewer className="relative z-10" pdf={activeFrame} />
          {imageEl ? (
            <MediaImage asBackground classNames={{ root: 'z-0' }} image={imageEl} />
          ) : null}
        </>
      ) : null}
    </section>
  );
}

export default Menu;
